import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    onAuthChange,
    signUpWithEmail,
    signInWithEmail,
    signInWithGoogle,
    logOut,
    getUserData,
    updateStreak
} from '../services/firebase';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Listen for auth state changes
    useEffect(() => {
        const unsubscribe = onAuthChange(async (user) => {
            setCurrentUser(user);

            if (user) {
                // Update streak first
                await updateStreak(user.uid);

                // Then fetch user data from Firestore (includes updated streak)
                const result = await getUserData(user.uid);
                if (result.success) {
                    setUserData(result.data);
                } else {
                    console.error('Error fetching user data:', result.error);
                }
            } else {
                setUserData(null);
            }

            setLoading(false);
        });

        return unsubscribe;
    }, []);

    // Refresh user data
    const refreshUserData = async () => {
        if (currentUser) {
            const result = await getUserData(currentUser.uid);
            if (result.success) {
                setUserData(result.data);
            }
        }
    };

    const signup = async (email, password, displayName) => {
        setError(null);
        const result = await signUpWithEmail(email, password, displayName);

        if (!result.success) {
            setError(result.error);
        }

        return result;
    };

    const login = async (email, password) => {
        setError(null);
        const result = await signInWithEmail(email, password);

        if (!result.success) {
            setError(result.error);
        }

        return result;
    };

    const loginWithGoogle = async () => {
        setError(null);
        const result = await signInWithGoogle();

        if (!result.success) {
            setError(result.error);
        }

        return result;
    };

    const logout = async () => {
        setError(null);
        const result = await logOut();

        if (!result.success) {
            setError(result.error);
        }

        return result;
    };

    const value = {
        currentUser,
        userData,
        loading,
        error,
        signup,
        login,
        loginWithGoogle,
        logout,
        refreshUserData
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
